<template>
  <div id="app" class="main-container">
    <div class="container">
      <ControlGroup>
        <DataGetter
          :url="`${apiRoute}/list/Country`"
          v-slot="{ data }"
          :child-element-type="null"
        >
          <Dropdown
            key="countrydd"
            label="Country/Region"
            v-model="country"
            :options="data || []"
          />
        </DataGetter>
      </ControlGroup>

      <span class="section-title">
        <p class="f-ui-1-bold">
          Annual heat demand

          <ReferenceModal :refIndex="1" modalTitle="Annual heat demand">
            <template v-slot:reference>
              <Icon icon="help" />
            </template>
            <p>
              Annual heat demand corresponds to the service demand for space and
              domestic hot water heating.
            </p>
            <p>
              The default values suggested in the tool correspond to the
              national average annual dwelling heat demand, expressed in MWh per
              dwelling per year. These values are based on:
              <a
                href="https://www.iea.org/data-and-statistics/data-product/energy-efficiency-indicators"
              >
                IEA Energy efficiency indicators (2021) </a
              >.
            </p>
            <p>
              There are large variations in heat demand across building fleets,
              depending on the type of dwelling (apartment, single-family house,
              etc.), the efficiency of the building envelope, the size of the
              dwelling and the behaviours of occupants.
            </p>
            <p>
              In this section, you have the possibility to adjust space and
              water heating demand assumptions.
            </p>
          </ReferenceModal>
        </p>
        <a
          @click="() => (edit.demand = !edit.demand)"
          class="m-nav-page__infos-close-btn"
          title="close"
        >
          <span class="f-ui-2">{{
            edit.demand ? "Hide assumptions" : "Edit assumptions"
          }}</span>
          <Icon :icon="edit.demand ? 'chevron-up' : 'chevron-down'" />
        </a>
      </span>
      <ControlGroup class="sub-control-group">
        <DataGetter
          :url="`${apiRoute}`"
          class="demand-group"
          :params="{
            Item: [
              'Annual heat demand - Space heating',
              'Annual heat demand - Water heating',
            ],
            Country: country,
          }"
          v-slot="{ data }"
          @fetched="($event) => paramDataFetched($event, 'Item', 'heatDemand')"
        >
          <template v-if="edit.demand">
            <Slider
              v-for="(elt, index) in data"
              :key="`demandslider-${index}`"
              :title="elt.Item"
              :units="elt.Unit"
              :min="0"
              :max="elt.Item.includes('Space') ? 30 : 10"
              :decimals="2"
              :step="0.01"
              v-model="parameterValues.heatDemand[elt.Item]"
            />
          </template>
          <template v-else>
            <div
              class="a-input slider"
              v-for="(elt, index) in data"
              :key="`demandslider-${index}`"
            >
              <span class="f-ui-2-bold">{{ elt.Item }}</span>
              <span class="f-ui-2">
                {{ parameterValues.heatDemand[elt.Item].toFixed(2) }}
                {{ elt.Unit }}
              </span>
            </div>
          </template>
        </DataGetter>
      </ControlGroup>

      <span class="section-title">
        <p class="f-ui-1-bold">
          Hybrid systems with solar thermal

          <ReferenceModal
            :refIndex="2"
            modalTitle="Hybrid systems with solar thermal"
          >
            <template v-slot:reference>
              <Icon icon="help" />
            </template>
            <p>
              Solar thermal systems can be combined with other heating
              technologies to provide space heat (solar combi systems) and hot
              water (solar combi systems and solar thermal water heaters). In
              this simulation tool, you have the possibility to explore the
              economics of such hybrid solar systems by combining non-solar
              technologies with either a combined solar thermal system or a
              solar water heater. In the first case, the combined solar thermal
              system is assumed to supply 50% of total space and water heat
              demand; in the second case, the solar thermal water heater is
              assumed to contribute 70% of the domestic hot water demand.
              Figures display results according to the option selected.
            </p>
          </ReferenceModal>
        </p>
        <a
          @click="() => (edit.hybrid = !edit.hybrid)"
          class="m-nav-page__infos-close-btn"
          title="close"
        >
          <span class="f-ui-2">{{
            edit.hybrid ? "Hide assumptions" : "Edit assumptions"
          }}</span
          ><Icon :icon="edit.hybrid ? 'chevron-up' : 'chevron-down'" />
        </a>
      </span>
      <RadioGroup
        v-if="edit.hybrid"
        v-model="techCase"
        class="radio-group"
        :options="[
          {
            value: 'combiSolar',
            label: 'Combined solar thermal system covering 50% of heat uses',
          },
          {
            value: 'solarThermal',
            label: 'Solar thermal water heater covering 70% of heat uses',
          },
          { value: 'default', label: 'None' },
        ]"
      />
      <ControlGroup v-else class="radio-group">
        <span class="a-radio__label" v-if="techCase === 'combiSolar'">
          Combined solar thermal system covering 50% of heat uses
        </span>
        <span class="a-radio__label" v-else-if="techCase === 'solarThermal'">
          Solar thermal water heater covering 70% of heat uses
        </span>
        <span class="a-radio__label" v-else> None </span>
      </ControlGroup>

      <span class="section-title">
        <p class="f-ui-1-bold">
          Technologies

          <ReferenceModal :refIndex="3" modalTitle="Technologies">
            <template v-slot:reference>
              <Icon icon="help" />
            </template>
            <p>
              In this section, you can modify assumptions regarding technology
              lifetime, total investment costs, and average annual fixed
              operating and maintenance costs. The default values provided here
              are conventional values provided for information.
            </p>
            <p>
              Equipment lifetime varies depending on the quality of the
              equipment and of the installation, climate and average annual load
              hours and regular maintenance operations. Please note that in the
              case of a ground-source heat pump, for which the underground heat
              exchanger can last for over 50 years, lifetime refers to the
              lifetime of the compressor unit.
            </p>
            <p>
              Total investment costs vary significantly from one installation to
              another, depending on multiple parameters, including the heating
              equipment quality and features, its capacity, the availability of
              installers, the need for ancillary work (e.g. heat distribution
              system inside the dwelling), etc. You have the possibility to
              adjust these assumptions and explore how they affect the
              cost-competitiveness of residential heating technologies.
            </p>
            <p>
              Please note that investment costs for ground source heat pumps
              also include the cost of the underground heat exchanger. For the
              sake of simplicity, the calculation of the discounted cashflow
              assumes nominal cost of replacement for the heat pump unit (i.e.
              excluding the heat exchanger) at 40% of the total initial
              investment cost.
            </p>
          </ReferenceModal>
        </p>
        <a
          @click="() => (edit.techs = !edit.techs)"
          class="m-nav-page__infos-close-btn"
          title="close"
        >
          <span class="f-ui-2">{{
            edit.techs ? "Hide assumptions" : "Edit assumptions"
          }}</span
          ><Icon :icon="edit.techs ? 'chevron-up' : 'chevron-down'" />
        </a>
      </span>
      <DataGetter
        :url="`${apiRoute}/list/Technology`"
        :params="{
          Item: 'Total investment cost',
          Country: country,
        }"
        v-slot="{ data }"
      >
        <div
          class="sub-control-group m-chart-block__container--tints"
          v-for="(tech, index) in data"
          :key="`techno-${index}`"
        >
          <p class="f-ui-1-bold" style="display: flex; align-items: center">
            {{ tech }}
            <svg
              :class="`highcharts-color-${technologyColorIndex[tech]}`"
              style="width: 1rem; height: 1rem; margin-left: 12px"
            >
              <circle cx="50%" cy="50%" r="5" />
            </svg>
          </p>
          <DataGetter
            :url="`${apiRoute}`"
            class="technology-group"
            :params="{
              Technology: tech,
              Country: country,
              Item: [
                'Total investment cost',
                'Annual fixed O&M cost',
                'Technology lifetime',
              ],
            }"
            v-slot="{ data }"
            @fetched="
              ($event) => paramDataFetched($event, 'Item', 'techs', tech)
            "
          >
            <template v-if="edit.techs">
              <Slider
                v-for="(elt, i) in data"
                :key="`${tech}-slider-${i}`"
                :title="elt.Item"
                :units="elt.Unit"
                :min="technologyMinMax[tech].min[elt.Item]"
                :max="technologyMinMax[tech].max[elt.Item]"
                :decimals="0"
                v-model="parameterValues.techs[tech][elt.Item]"
              />
            </template>
            <template v-else>
              <div
                class="a-input slider"
                v-for="(elt, i) in data"
                :key="`${tech}-slider-${i}`"
              >
                <span class="f-ui-2-bold">
                  {{ elt.Item }}
                </span>
                <span class="f-ui-2">
                  {{ parameterValues.techs[tech][elt.Item].toFixed(0) }}
                  {{ elt.Unit }}
                </span>
              </div>
            </template>
          </DataGetter>
        </div>
      </DataGetter>

      <span class="section-title">
        <p class="f-ui-1-bold">
          Fuel price scenario

          <ReferenceModal :refIndex="4" modalTitle="Fuel price scenario">
            <template v-slot:reference>
              <Icon icon="help" />
            </template>
            <p>
              Default values for fuel prices correspond to the average 2019
              end-consumer prices for the selected country, expressed in USD per
              MWh. These values are based on:
              <a
                href="https://www.iea.org/data-and-statistics/data-product/energy-prices"
              >
                IEA Energy prices (2021) </a
              >.
            </p>
            <p>
              You can modify fuel price projections by changing the base-year
              and end-year values. For the sake of simplicity, fuel prices are
              assumed to evolve linearly between the base-year and end-year
              values.
            </p>
          </ReferenceModal>
        </p>
        <a
          @click="() => (edit.fuels = !edit.fuels)"
          class="m-nav-page__infos-close-btn"
          title="close"
        >
          <span class="f-ui-2">{{
            edit.fuels ? "Hide assumptions" : "Edit assumptions"
          }}</span
          ><Icon :icon="edit.fuels ? 'chevron-up' : 'chevron-down'" />
        </a>
      </span>
      <ControlGroup>
        <DataGetter
          :url="`${apiRoute}`"
          class="fuel-group"
          :params="{
            Country: country,
            Item: 'End-user fuel cost',
          }"
          v-slot="{ data }"
          @fetched="
            ($event) => {
              paramDataFetched($event, 'Technology', 'fuels', 'base');
              paramDataFetched($event, 'Technology', 'fuels', 'end');
            }
          "
        >
          <template v-if="edit.fuels">
            <div v-for="(elt, index) in data" :key="`fuel-${index}`">
              <p class="f-ui-1-bold">{{ elt.Technology }}</p>
              <StyledInput
                label="Base price"
                v-model="valuesFuel.base[elt.Technology]"
                type="number"
                suffix="<span class='f-ui-3'>USD/MWh</span>"
              />
              <StyledInput
                label="End price"
                v-model="valuesFuel.end[elt.Technology]"
                type="number"
                suffix="<span class='f-ui-3'>USD/MWh</span>"
              />
            </div>
          </template>
          <template v-else>
            <div class="m-chart-block" style="padding: 0">
              <Chart
                key="indicator-chart-fuels"
                :options="{
                  series: fuelsGraphData.series,
                  ...chartOptions('line', 'USD/MWh', {}),
                  chart: {
                    spacingTop: 30,
                    spacingBottom: 30,
                    spacingRight: 20,
                    height: 260,
                    styledMode: true,
                    type: 'line',
                  },
                  legend: {
                    enabled: true,
                    layout: 'proximate',
                    align: 'right',
                  },
                  xAxis: {
                    // categories: fuelsGraphData.xAxisCategories,
                  },
                }"
                class="highcharts-outline-black m-chart__item"
              />
            </div>
          </template>
        </DataGetter>
      </ControlGroup>

      <span class="section-title">
        <p class="f-ui-1-bold">
          Fuel CO2 content scenario

          <ReferenceModal :refIndex="5" modalTitle="Fuel CO2 content scenario">
            <template v-slot:reference>
              <Icon icon="help" />
            </template>
            <p>
              The default values provided for the CO2 content of oil and gas
              correspond to the direct CO2 emissions released through the
              combustion of a quantity of fuel corresponding to an energy
              content of 1MWh (using higher calorific value of the fuel). They
              do not reflect the whole life-cycle emissions, i.e. greenhouse gas
              emissions generated for the extraction and transport of the fuel.
            </p>
            <p>
              The default value provided for the CO2 content of electricity
              corresponds to the annual average CO2 intensity of the national
              electricity generation mix for the year 2019.
            </p>
            <p>
              The default value for the CO2 content of wood pellets is assumed
              to be null, which excludes the impacts related to the collection,
              transport and processing of biomass into pellets.
            </p>
            <p>
              You can modify these assumptions by changing the base-year and
              end-year values; for the sake of simplicity, CO2 content values
              are assumed to evolve linearly between the base-year and the
              end-year.
            </p>
          </ReferenceModal>
        </p>
        <a
          @click="() => (edit.co2content = !edit.co2content)"
          class="m-nav-page__infos-close-btn"
          title="close"
        >
          <span class="f-ui-2">{{
            edit.co2content ? "Hide assumptions" : "Edit assumptions"
          }}</span
          ><Icon :icon="edit.co2content ? 'chevron-up' : 'chevron-down'" />
        </a>
      </span>
      <ControlGroup>
        <DataGetter
          :url="`${apiRoute}`"
          class="fuel-group"
          :params="{
            Country: country,
            Item: 'Fuel CO? content',
          }"
          v-slot="{ data }"
          @fetched="
            ($event) => {
              paramDataFetched($event, 'Technology', 'co2content', 'base');
              paramDataFetched($event, 'Technology', 'co2content', 'end');
            }
          "
        >
          <template v-if="edit.co2content">
            <div v-for="(elt, index) in data" :key="`fuel-${index}`">
              <p class="f-ui-1-bold">{{ elt.Technology }}</p>
              <StyledInput
                label="Base CO2 content"
                v-model="valuesCO2Content.base[elt.Technology]"
                type="number"
                suffix="<span class='f-ui-3'>kgCO2/MWh</span>"
              />
              <StyledInput
                label="End CO2 content"
                v-model="valuesCO2Content.end[elt.Technology]"
                type="number"
                suffix="<span class='f-ui-3'>kgCO2/MWh</span>"
              />
            </div>
          </template>
          <template v-else>
            <div class="m-chart-block" style="padding: 0">
              <Chart
                key="indicator-chart-co2content"
                :options="{
                  series: CO2ContentGraphData.series,
                  ...chartOptions('line', 'kgCO2/MWh', {}),
                  chart: {
                    spacingTop: 30,
                    spacingBottom: 30,
                    spacingRight: 20,
                    height: 260,
                    styledMode: true,
                    type: 'line',
                  },
                  legend: {
                    enabled: true,
                    layout: 'proximate',
                    align: 'right',
                  },
                  xAxis: {
                    // categories: CO2ContentGraphData.xAxisCategories,
                  },
                }"
                class="highcharts-outline-black m-chart__item"
              />
            </div>
          </template>
        </DataGetter>
      </ControlGroup>

      <span class="section-title">
        <p class="f-ui-1-bold">
          Carbon tax

          <ReferenceModal :refIndex="6" modalTitle="Carbon tax">
            <template v-slot:reference>
              <Icon icon="help" />
            </template>
            <p>
              You have the possibility to assume the implementation of a carbon
              tax by setting a carbon price expressed in USD per ton of CO2 and
              an implementation date. Please note that some countries (for
              instance Sweden) already have a carbon tax in force, which is
              reflected in the default values provided for end-consumer fuel
              prices.
            </p>
          </ReferenceModal>
        </p>
        <a
          @click="() => (edit.tax = !edit.tax)"
          class="m-nav-page__infos-close-btn"
          title="close"
        >
          <span class="f-ui-2">{{
            edit.tax ? "Hide assumptions" : "Edit assumptions"
          }}</span
          ><Icon :icon="edit.tax ? 'chevron-up' : 'chevron-down'" />
        </a>
      </span>
      <ControlGroup v-if="edit.tax">
        <StyledInput
          label="Price"
          v-model="carbonTax"
          type="number"
          suffix="<span class='f-ui-3'>USD/ton CO2</span>"
        />
        <StyledInput
          label="Year of implementation"
          v-model="carbonTaxDate"
          type="number"
        />
      </ControlGroup>
      <ControlGroup v-else>
        <div class="a-input slider">
          <span class="f-ui-2-bold">Price</span>
          <span class="f-ui-2"> {{ carbonTax }} USD/ton CO2 </span>
        </div>
        <div class="a-input slider">
          <span class="f-ui-2-bold">Year of implementation</span>
          <span class="f-ui-2">
            {{ carbonTaxDate }}
          </span>
        </div>
      </ControlGroup>

      <span class="section-title">
        <p class="f-ui-1-bold">
          Other

          <ReferenceModal :refIndex="7" modalTitle="Other">
            <template v-slot:reference>
              <Icon icon="help" />
            </template>
            <p>
              In this section, you can assume the availability of investment
              subsidies, and adjust the discount rate considered for the
              calculation of the discounted cumulative cash flows and levelized
              costs of heating.
            </p>
          </ReferenceModal>
        </p>
        <a
          @click="() => (edit.other = !edit.other)"
          class="m-nav-page__infos-close-btn"
          title="close"
        >
          <Icon :icon="edit.other ? 'chevron-up' : 'chevron-down'" />
        </a>
      </span>
      <template v-if="edit.other">
        <ControlGroup>
          <StyledInput
            label="Investment subsidy"
            v-model="subsidy"
            type="number"
            suffix="<span class='f-ui-3'>USD</span>"
          />

          <StyledInput
            label="Discount rate"
            v-model="discountRate"
            type="number"
            suffix="<span class='f-ui-3'>%</span>"
          />
        </ControlGroup>
      </template>
      <template v-else>
        <ControlGroup>
          <div class="a-input slider">
            <span class="f-ui-2-bold">Investment subsidy</span>
            <span class="f-ui-2"> {{ subsidy }} USD </span>
          </div>
          <div class="a-input slider">
            <span class="f-ui-2-bold">Discount rate</span>
            <span class="f-ui-2" v-html="` ${discountRate}&nbsp;%`"></span>
          </div>
        </ControlGroup>
      </template>

      <StyledButton
        :onClick="resetParamsToDefault"
        :primary="false"
        style="float: right"
      >
        Reset
      </StyledButton>
    </div>

    <div class="container">
      <ButtonGroup
        class="button-group"
        :options="[
          { value: 1, label: 'Discounted cumulative cash flow' },
          { value: 2, label: 'Levelized cost of heating' },
          { value: 3, label: 'Cumulative CO2 emissions' },
        ]"
        v-model="viewMode"
      />

      <div
        class="m-block--charts m-block--charts-full m-chart-block__container--tints"
        v-if="viewMode === 1"
      >
        <div class="m-chart-block">
          <header>
            <span>
              <span class="f-ui-2-bold">
                {{ cumulativeDCFTitle }}
              </span>
              <p class="m-chart__units">USD</p>
            </span>
          </header>

          <Chart
            v-if="cumulativeDCFData && cumulativeDCFData.slicedData.length"
            key="indicator-chart-1"
            :options="{
              ...chartOptions('line', 'USD', {}),
              series: cumulativeDCFData.slicedData,
              xAxis: {
                // categories: cumulativeDCFData.xAxisCategories,
              },
            }"
            class="highcharts-outline-black m-chart__item"
          />
          <div v-else class="f-ui-2-bold" style="text-align: center">
            No data
          </div>
          <div class="m-chart__credits">
            <small class="m-chart__credit f-ui-3">
              IEA. All rights reserved.
            </small>
          </div>

          <div class="m-chart-metadata">
            <div class="m-chart-metadata__item">
              <span class="f-ui-3">Notes</span>
              <p class="f-ui-3">
                This figure illustrates the cumulative sum of discounted cash
                flows associated with each heating technology. It accounts for
                the consumer investment costs (including V.A.T.), investment
                subsidies, fixed operational and maintenance costs, fuel costs,
                and – depending on your assumptions – the cost of a carbon tax.
                <br />
                The calculation assumes the replacement of the entire heating
                system at the end of its lifetime, except for ground source heat
                pumps. For ground-source heat pumps, only the compressor unit is
                replaced while the underground heat exchanger is assumed to
                remain operational. We assume the cost of the heat pump
                compressor unit to represent 40% of the total initial investment
                cost for ground source heat pumps.
                <br />
                Please note that pellet stove and air-air heat pumps only meet
                space heating demand.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="m-block--charts m-block--charts-full"
        v-else-if="viewMode === 2"
      >
        <div class="m-chart-block">
          <header>
            <span>
              <span class="f-ui-2-bold">
                {{ levelizedCostOfHeatingTitle }}
              </span>
              <p class="m-chart__units">USD/MWh</p>
            </span>
          </header>

          <Chart
            v-if="
              levelizedCostOfHeatingData &&
              levelizedCostOfHeatingData.slicedData.length
            "
            key="indicator-chart-2"
            :options="{
              ...chartOptions('bar', 'USD/MWh', {}),
              series: levelizedCostOfHeatingData.slicedData,
              xAxis: {
                type: 'category',
                categories: levelizedCostOfHeatingData.xAxisCategories,
              },
              tooltip: {
                formatter: function () {
                  const customTooltip = [
                    `<span class='highcharts-header'>${
                      this.x
                    } <span class='highcharts-color-${
                      technologyColorIndex[this.x]
                    }'>●</span></span><br/>`,
                    `<span class='highcharts-color-${
                      this.colorIndex
                    }'>●</span> ${this.series.name}: <b>${this.y
                      .toLocaleString('FR', {
                        maximumFractionDigits: 1,
                        minimumFractionDigits: 1,
                      })
                      .replace(',', '.')} USD/MWh</b><br/>`,
                    `<span>●</span> Total: <b>${this.total
                      .toLocaleString('FR', {
                        maximumFractionDigits: 1,
                        minimumFractionDigits: 1,
                      })
                      .replace(',', '.')} USD/MWh</b><br/>`,
                  ];
                  return customTooltip;
                },
              },
            }"
            class="highcharts-outline-black m-chart__item"
          />
          <div v-else class="f-ui-2-bold" style="text-align: center">
            No data
          </div>
          <div class="m-chart__credits">
            <small class="m-chart__credit f-ui-3">
              IEA. All rights reserved.
            </small>
          </div>

          <div class="m-chart-metadata">
            <div class="m-chart-metadata__item">
              <span class="f-ui-3">Notes</span>
              <p class="f-ui-3">
                This figure illustrates the levelized cost of heating (LCOH) for
                the different heating technologies. It is calculated as the
                ratio of the cumulative discounted cash flow over the lifetime
                of the technology to the total amount of heat supplied by the
                system over its lifetime.
                <br />
                In the case of geothermal heat pumps, the calculation is based
                on the lifetime of the compressor unit, and only accounts for
                60% of the total investment cost, in order to reflect the longer
                lifetime of the underground heat exchanger, which is assumed to
                last for more than three generations of compressors.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="m-block--charts m-block--charts-full m-chart-block__container--tints"
        v-else-if="viewMode === 3"
      >
        <div class="m-chart-block">
          <header>
            <span>
              <span class="f-ui-2-bold">
                {{ cumulativeCO2EmissionsTitle }}
              </span>
              <p class="m-chart__units">tons CO2</p>
            </span>
          </header>

          <Chart
            v-if="
              cumulativeCO2EmissionsData &&
              cumulativeCO2EmissionsData.slicedData.length
            "
            key="indicator-chart-3"
            :options="{
              ...chartOptions('line', 'tons CO2', {}),
              series: cumulativeCO2EmissionsData.slicedData,
              xAxis: {
                // categories: cumulativeCO2EmissionsData.xAxisCategories,
              },
            }"
            class="highcharts-outline-black m-chart__item"
          />
          <div v-else class="f-ui-2-bold" style="text-align: center">
            No data
          </div>
          <div class="m-chart__credits">
            <small class="m-chart__credit f-ui-3">
              IEA. All rights reserved.
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global Highcharts */
import { Chart } from "highcharts-vue";
import {
  ButtonGroup,
  DataGetter,
  ControlGroup,
  Dropdown,
  RadioGroup,
  StyledInput,
  Slider,
  StyledButton,
  Icon,
  ReferenceModal,
} from "@international-energy-agency/vue-ui";
import ChartOptions from "@international-energy-agency/vue-ui/highcharts";
import axios from "axios";
import {
  technologyMinMax,
  fuelTypes,
  technologyColorIndex,
} from "./data/constants";
Highcharts.setOptions({
  lang: {
    numericSymbols: null,
  },
});
export default {
  name: "App",
  components: {
    ButtonGroup,
    DataGetter,
    Chart,
    ControlGroup,
    Dropdown,
    StyledInput,
    Slider,
    RadioGroup,
    StyledButton,
    Icon,
    ReferenceModal,
  },
  props: {
    test: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      apiRoute: this.test
        ? "https://api.test.iea.org/heat-economics"
        : "https://api.iea.org/heat-economics",
      // apiRoute: "http://localhost:3000/heat-economics",
      viewMode: 1,
      technologyMinMax,
      technologyColorIndex,
      efficiencyData: [],
      costShareData: [],
      parameterValues: {
        heatDemand: {
          "Annual heat demand - Space heating": 0,
          "Annual heat demand - Water heating": 0,
        },
        techs: {
          "Oil condensing boiler": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Gas condensing boiler": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Air-air heat pump": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Air-water heat pump": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Ground-source heat pump": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Pellet Stove": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Pellet boiler (automated feed)": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Pellet boiler (manual feed)": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Solar thermal combi system": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Solar thermal water heater": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
          "Thermally-driven air-water heat pump": {
            "Annual fixed O&M cost": 0,
            "Technology lifetime": 0,
            "Total investment cost": 0,
          },
        },
        fuels: {
          base: {
            Electricity: 0,
            Gas: 0,
            Oil: 0,
            Pellet: 0,
          },
          end: {
            Electricity: 0,
            Gas: 0,
            Oil: 0,
            Pellet: 0,
          },
        },
        co2content: {
          base: {
            Electricity: 0,
            Gas: 0,
            Oil: 0,
            Pellet: 0,
          },
          end: {
            Electricity: 0,
            Gas: 0,
            Oil: 0,
            Pellet: 0,
          },
        },
      },
      country: "France",
      carbonTax: 0,
      carbonTaxDate: 2025,
      subsidy: 0,
      discountRate: 0,
      techCase: "default",
      defaultValues: {
        parameterValues: {
          heatDemand: {
            "Annual heat demand - Space heating": 0,
            "Annual heat demand - Water heating": 0,
          },
          techs: {
            "Oil condensing boiler": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Gas condensing boiler": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Air-air heat pump": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Air-water heat pump": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Ground-source heat pump": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Pellet Stove": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Pellet boiler (automated feed)": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Pellet boiler (manual feed)": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Solar thermal combi system": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Solar thermal water heater": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
            "Thermally-driven air-water heat pump": {
              "Annual fixed O&M cost": 0,
              "Technology lifetime": 0,
              "Total investment cost": 0,
            },
          },
          fuels: {
            base: {
              Electricity: 0,
              Gas: 0,
              Oil: 0,
              Pellet: 0,
            },
            end: {
              Electricity: 0,
              Gas: 0,
              Oil: 0,
              Pellet: 0,
            },
          },
          co2content: {
            base: {
              Electricity: 0,
              Gas: 0,
              Oil: 0,
              Pellet: 0,
            },
            end: {
              Electricity: 0,
              Gas: 0,
              Oil: 0,
              Pellet: 0,
            },
          },
        },
        carbonTax: 0,
        carbonTaxDate: 2025,
        subsidy: 0,
        discountRate: 0,
        techCase: "default",
      },
      edit: {
        demand: false,
        hybrid: false,
        techs: false,
        fuels: false,
        co2content: false,
        tax: false,
        other: false,
      },
    };
  },
  created() {
    this.getData();
  },
  watch: {
    country() {
      this.getData();
    },
  },
  computed: {
    dateRange() {
      const year = new Date().getFullYear();
      const range = [...Array(50).keys()];
      return range.map((i) => year + i);
    },
    annualHeatDemand() {
      if (this.techCase === "default") {
        return {
          "Oil condensing boiler":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ],
          "Gas condensing boiler":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ],
          "Air-air heat pump": this.parameterValues.heatDemand[
            "Annual heat demand - Space heating"
          ],
          "Air-water heat pump":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ],
          "Ground-source heat pump":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ],
          "Pellet Stove":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ],
          "Pellet boiler (automated feed)":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ],
          "Pellet boiler (manual feed)":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ],
          "Thermally-driven air-water heat pump":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ],
        };
      }
      if (this.techCase === "combiSolar") {
        return {
          "Oil condensing boiler":
            (this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
              this.parameterValues.heatDemand[
                "Annual heat demand - Water heating"
              ]) *
            0.5,
          "Gas condensing boiler":
            (this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
              this.parameterValues.heatDemand[
                "Annual heat demand - Water heating"
              ]) *
            0.5,
          "Air-air heat pump":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] * 0.5,
          "Air-water heat pump":
            (this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
              this.parameterValues.heatDemand[
                "Annual heat demand - Water heating"
              ]) *
            0.5,
          "Ground-source heat pump":
            (this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
              this.parameterValues.heatDemand[
                "Annual heat demand - Water heating"
              ]) *
            0.5,
          "Pellet Stove":
            (this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
              this.parameterValues.heatDemand[
                "Annual heat demand - Water heating"
              ]) *
            0.5,
          "Pellet boiler (automated feed)":
            (this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
              this.parameterValues.heatDemand[
                "Annual heat demand - Water heating"
              ]) *
            0.5,
          "Pellet boiler (manual feed)":
            (this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
              this.parameterValues.heatDemand[
                "Annual heat demand - Water heating"
              ]) *
            0.5,
          "Thermally-driven air-water heat pump":
            (this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
              this.parameterValues.heatDemand[
                "Annual heat demand - Water heating"
              ]) *
            0.5,
        };
      }
      if (this.techCase === "solarThermal") {
        return {
          "Oil condensing boiler":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ] *
              0.3,
          "Gas condensing boiler":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ] *
              0.3,
          "Air-air heat pump": this.parameterValues.heatDemand[
            "Annual heat demand - Space heating"
          ],
          "Air-water heat pump":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ] *
              0.3,
          "Ground-source heat pump":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ] *
              0.3,
          "Pellet Stove":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ] *
              0.3,
          "Pellet boiler (automated feed)":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ] *
              0.3,
          "Pellet boiler (manual feed)":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ] *
              0.3,
          "Thermally-driven air-water heat pump":
            this.parameterValues.heatDemand[
              "Annual heat demand - Space heating"
            ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ] *
              0.3,
        };
      }
      return {};
    },
    valuesTech() {
      return this.parameterValues.techs;
    },
    valuesFuel() {
      return this.parameterValues.fuels;
    },
    valuesCO2Content() {
      return this.parameterValues.co2content;
    },
    cumulativeDCFTitle() {
      return `Discounted cumulative cash flow${
        this.techCase === "combiSolar"
          ? ", hybrid systems with solar combi"
          : ""
      }${
        this.techCase === "solarThermal"
          ? ", hybrid systems with solar thermal"
          : ""
      }`;
    },
    cumulativeDCFData() {
      const isSolarCombi = this.techCase === "combiSolar";
      const investmentCostSolarCombi = this.valuesTech
        ? this.valuesTech["Solar thermal combi system"]["Total investment cost"]
        : 0;
      const OMCostSolarCombi = this.valuesTech
        ? this.valuesTech["Solar thermal combi system"]["Annual fixed O&M cost"]
        : 0;
      const lifetimeSolarCombi = this.valuesTech
        ? this.valuesTech["Solar thermal combi system"]["Technology lifetime"]
        : 0;
      const isSolarThermal = this.techCase === "solarThermal";
      const investmentCostSolarThermal = this.valuesTech
        ? this.valuesTech["Solar thermal water heater"]["Total investment cost"]
        : 0;
      const OMCostSolarThermal = this.valuesTech
        ? this.valuesTech["Solar thermal water heater"]["Annual fixed O&M cost"]
        : 0;
      const lifetimeSolarThermal = this.valuesTech
        ? this.valuesTech["Solar thermal water heater"]["Technology lifetime"]
        : 0;

      const xAxisCategories = this.dateRange;
      const yAxisCategories = Object.keys(this.annualHeatDemand);

      const slicedData = yAxisCategories.map((seriesName) => {
        const data = [];
        let value = 0;
        const investmentCost = this.valuesTech
          ? this.valuesTech[seriesName]["Total investment cost"]
          : 0;
        const OMCost = this.valuesTech
          ? this.valuesTech[seriesName]["Annual fixed O&M cost"]
          : 0;
        const lifetime = this.valuesTech
          ? this.valuesTech[seriesName]["Technology lifetime"]
          : 0;

        const efficiency =
          this.efficiencyData && this.efficiencyData.length
            ? this.efficiencyData.find((row) => row.Technology === seriesName)
                .Value
            : 0;

        const costShare =
          this.costShareData && this.costShareData.length
            ? this.costShareData.find((row) => row.Technology === seriesName)
                .Value
            : 0;

        xAxisCategories.forEach((year, index) => {
          const i = index + 1;
          const fuelPrice = this.fuelsGraphData.series.find(
            (s) => s.name === fuelTypes[seriesName]
          ).data[index];

          const fuelCost =
            (this.annualHeatDemand[seriesName] * fuelPrice) / efficiency;

          const fuelContent =
            this.CO2ContentGraphData.series.find(
              (s) => s.name === fuelTypes[seriesName]
            ).data[index] / 1000;

          const CO2Tax =
            fuelContent *
            (this.annualHeatDemand[seriesName] / efficiency) *
            (year >= parseInt(this.carbonTaxDate) ? this.carbonTax : 0);

          const yearValue =
            (OMCost +
              (isSolarCombi ? OMCostSolarCombi : 0) +
              (isSolarThermal ? OMCostSolarThermal : 0) +
              fuelCost +
              CO2Tax +
              (i % lifetime === 0 ? investmentCost * (1 - costShare) : 0) +
              (i % lifetimeSolarCombi === 0 && isSolarCombi
                ? investmentCostSolarCombi
                : 0) +
              (i % lifetimeSolarThermal === 0 && isSolarThermal
                ? investmentCostSolarThermal
                : 0)) /
            Math.pow(1 + this.discountRate / 100, i);
          value += yearValue;
          data.push([
            year,
            investmentCost +
              (isSolarCombi ? investmentCostSolarCombi : 0) +
              (isSolarThermal ? investmentCostSolarThermal : 0) -
              this.subsidy +
              value,
          ]);
        });

        return {
          name: seriesName,
          data,
          colorIndex: technologyColorIndex[seriesName],
        };
      });
      return { slicedData, xAxisCategories };
    },
    levelizedCostOfHeatingTitle() {
      return `Levelized cost of heating over the lifetime of the technology${
        this.techCase === "combiSolar"
          ? ", hybrid systems with solar combi"
          : ""
      }${
        this.techCase === "solarThermal"
          ? ", hybrid systems with solar thermal"
          : ""
      }`;
    },
    levelizedCostOfHeatingData() {
      const isSolarCombi = this.techCase === "combiSolar";
      const investmentCostSolarCombi =
        this.valuesTech && isSolarCombi
          ? this.valuesTech["Solar thermal combi system"][
              "Total investment cost"
            ]
          : 0;
      const OMCostSolarCombi =
        this.valuesTech && isSolarCombi
          ? this.valuesTech["Solar thermal combi system"][
              "Annual fixed O&M cost"
            ]
          : 0;
      const lifetimeSolarCombi =
        this.valuesTech && isSolarCombi
          ? this.valuesTech["Solar thermal combi system"]["Technology lifetime"]
          : 1;
      const heatDemandSolarCombi = isSolarCombi
        ? 0.5 *
          (this.parameterValues.heatDemand[
            "Annual heat demand - Space heating"
          ] +
            this.parameterValues.heatDemand[
              "Annual heat demand - Water heating"
            ])
        : 1;
      const isSolarThermal = this.techCase === "solarThermal";
      const investmentCostSolarThermal =
        this.valuesTech && isSolarThermal
          ? this.valuesTech["Solar thermal water heater"][
              "Total investment cost"
            ]
          : 0;
      const OMCostSolarThermal =
        this.valuesTech && isSolarThermal
          ? this.valuesTech["Solar thermal water heater"][
              "Annual fixed O&M cost"
            ]
          : 0;
      const lifetimeSolarThermal =
        this.valuesTech && isSolarThermal
          ? this.valuesTech["Solar thermal water heater"]["Technology lifetime"]
          : 1;
      const heatDemandSolarThermal = isSolarThermal
        ? 0.7 *
          this.parameterValues.heatDemand["Annual heat demand - Water heating"]
        : 1;

      const xAxisCategories = Object.keys(this.annualHeatDemand);

      const dataCapex = xAxisCategories.map((techName) => {
        const investmentCost = this.valuesTech
          ? this.valuesTech[techName]["Total investment cost"]
          : 0;
        const lifetime = this.valuesTech
          ? this.valuesTech[techName]["Technology lifetime"]
          : 1;
        const costShare =
          this.costShareData && this.costShareData.length
            ? this.costShareData.find((row) => row.Technology === techName)
                .Value
            : 0;

        const valueCapex =
          (investmentCost * (1 - costShare + costShare / 3) +
            investmentCostSolarCombi +
            investmentCostSolarThermal -
            this.subsidy) /
          (lifetime * this.annualHeatDemand[techName] +
            lifetimeSolarCombi * heatDemandSolarCombi +
            lifetimeSolarThermal * heatDemandSolarThermal);

        return [techName, valueCapex];
      });

      const dataOpex = xAxisCategories.map((techName) => {
        const lifetime = this.valuesTech
          ? this.valuesTech[techName]["Technology lifetime"]
          : 1;
        const OMCost = this.valuesTech
          ? this.valuesTech[techName]["Annual fixed O&M cost"]
          : 0;

        const efficiency = this.efficiencyData.find(
          (row) => row.Technology === techName
        ).Value;

        let sum = 0;
        this.dateRange
          .filter((year, index) => index < lifetime)
          .forEach((year, index) => {
            const i = index + 1;
            const fuelPrice = this.fuelsGraphData.series.find(
              (s) => s.name === fuelTypes[techName]
            ).data[index];

            const fuelCost =
              (this.annualHeatDemand[techName] * fuelPrice) / efficiency;

            const fuelContent =
              this.CO2ContentGraphData.series.find(
                (s) => s.name === fuelTypes[techName]
              ).data[index] / 1000;

            const CO2Tax =
              fuelContent *
              (this.annualHeatDemand[techName] / efficiency) *
              (year >= parseInt(this.carbonTaxDate) ? this.carbonTax : 0);

            const yearValue =
              (OMCost + fuelCost + CO2Tax) /
              Math.pow(1 + this.discountRate / 100, i);

            sum += yearValue;
          });

        let sumSolarCombi = 0;
        if (isSolarCombi) {
          this.dateRange
            .filter((year, index) => index < lifetimeSolarCombi)
            .forEach((year, index) => {
              const i = index + 1;

              const yearValue =
                OMCostSolarCombi / Math.pow(1 + this.discountRate / 100, i);

              sumSolarCombi += yearValue;
            });
        }

        let sumSolarThermal = 0;
        if (isSolarThermal) {
          this.dateRange
            .filter((year, index) => index < lifetimeSolarThermal)
            .forEach((year, index) => {
              const i = index + 1;

              const yearValue =
                OMCostSolarThermal / Math.pow(1 + this.discountRate / 100, i);

              sumSolarThermal += yearValue;
            });
        }

        const valueOpex =
          (sum + sumSolarCombi + sumSolarThermal) /
          (lifetime * this.annualHeatDemand[techName] +
            lifetimeSolarCombi * heatDemandSolarCombi +
            lifetimeSolarThermal * heatDemandSolarThermal);

        return [techName, valueOpex];
      });

      const slicedData = [
        {
          name: "LCOH CAPEX",
          data: dataCapex,
          colorIndex: 0,
        },
        {
          name: "LCOH OPEX",
          data: dataOpex,
          colorIndex: 1,
        },
      ];

      return { slicedData, xAxisCategories };
    },
    cumulativeCO2EmissionsTitle() {
      return `Cumulative CO2 emissions${
        this.techCase === "combiSolar"
          ? ", hybrid systems with solar combi"
          : ""
      }${
        this.techCase === "solarThermal"
          ? ", hybrid systems with solar thermal"
          : ""
      }`;
    },
    cumulativeCO2EmissionsData() {
      const xAxisCategories = this.dateRange;
      const yAxisCategories = Object.keys(this.annualHeatDemand);

      const slicedData = yAxisCategories.map((seriesName) => {
        const efficiency =
          this.efficiencyData && this.efficiencyData.length
            ? this.efficiencyData.find((row) => row.Technology === seriesName)
                .Value
            : 1;

        const data = xAxisCategories.map((year, index) => {
          const fuelContent =
            this.CO2ContentGraphData.series.find(
              (s) => s.name === fuelTypes[seriesName]
            ).data[index] / 1000;

          const value =
            (this.annualHeatDemand[seriesName] / efficiency) * fuelContent;
          return [year, value * (index + 1)];
        });

        return {
          name: seriesName,
          data,
          colorIndex: technologyColorIndex[seriesName],
        };
      });
      return { slicedData, xAxisCategories };
    },
    fuelsGraphData() {
      const fuelCoef = {
        Electricity:
          (parseFloat(this.valuesFuel.end["Electricity"]) -
            parseFloat(this.valuesFuel.base["Electricity"])) /
          (this.dateRange.length - 1),
        Gas:
          (parseFloat(this.valuesFuel.end["Gas"]) -
            parseFloat(this.valuesFuel.base["Gas"])) /
          (this.dateRange.length - 1),
        Oil:
          (parseFloat(this.valuesFuel.end["Oil"]) -
            parseFloat(this.valuesFuel.base["Oil"])) /
          (this.dateRange.length - 1),
        Pellet:
          (parseFloat(this.valuesFuel.end["Pellet"]) -
            parseFloat(this.valuesFuel.base["Pellet"])) /
          (this.dateRange.length - 1),
      };
      const series = [
        {
          name: "Electricity",
          data: this.dateRange.map(
            (year, index) =>
              parseFloat(this.valuesFuel.base["Electricity"]) +
              fuelCoef["Electricity"] * index
          ),
        },
        {
          name: "Gas",
          data: this.dateRange.map(
            (year, index) =>
              parseFloat(this.valuesFuel.base["Gas"]) + fuelCoef["Gas"] * index
          ),
        },
        {
          name: "Oil",
          data: this.dateRange.map(
            (year, index) =>
              parseFloat(this.valuesFuel.base["Oil"]) + fuelCoef["Oil"] * index
          ),
        },
        {
          name: "Pellet",
          data: this.dateRange.map(
            (year, index) =>
              parseFloat(this.valuesFuel.base["Pellet"]) +
              fuelCoef["Pellet"] * index
          ),
        },
      ];
      return { series, xAxisCategories: this.dateRange };
    },
    CO2ContentGraphData() {
      const fuelCoef = {
        Electricity:
          (parseFloat(this.valuesCO2Content.end["Electricity"]) -
            parseFloat(this.valuesCO2Content.base["Electricity"])) /
          (this.dateRange.length - 1),
        Gas:
          (parseFloat(this.valuesCO2Content.end["Gas"]) -
            parseFloat(this.valuesCO2Content.base["Gas"])) /
          (this.dateRange.length - 1),
        Oil:
          (parseFloat(this.valuesCO2Content.end["Oil"]) -
            parseFloat(this.valuesCO2Content.base["Oil"])) /
          (this.dateRange.length - 1),
        Pellet:
          (parseFloat(this.valuesCO2Content.end["Pellet"]) -
            parseFloat(this.valuesCO2Content.base["Pellet"])) /
          (this.dateRange.length - 1),
      };
      const series = [
        {
          name: "Electricity",
          data: this.dateRange.map(
            (year, index) =>
              parseFloat(this.valuesCO2Content.base["Electricity"]) +
              fuelCoef["Electricity"] * index
          ),
        },
        {
          name: "Gas",
          data: this.dateRange.map(
            (year, index) =>
              parseFloat(this.valuesCO2Content.base["Gas"]) +
              fuelCoef["Gas"] * index
          ),
        },
        {
          name: "Oil",
          data: this.dateRange.map(
            (year, index) =>
              parseFloat(this.valuesCO2Content.base["Oil"]) +
              fuelCoef["Oil"] * index
          ),
        },
        {
          name: "Pellet",
          data: this.dateRange.map(
            (year, index) =>
              parseFloat(this.valuesCO2Content.base["Pellet"]) +
              fuelCoef["Pellet"] * index
          ),
        },
      ];
      return { series, xAxisCategories: this.dateRange };
    },
  },
  methods: {
    getData(all = true) {
      if (all) {
        axios
          .get(this.apiRoute, {
            params: {
              Country: this.country,
              Item: "Efficiency",
            },
          })
          .then((res) => {
            this.efficiencyData = res.data;
          });

        axios
          .get(this.apiRoute, {
            params: {
              Country: this.country,
              Item: "CostShareHeatExchanger",
            },
          })
          .then((res) => {
            this.costShareData = res.data;
          });
      }
    },
    paramDataFetched(data, field, ...parameterPath) {
      let paramParent = this.parameterValues;
      let defaultParamParent = this.defaultValues.parameterValues;
      for (let path of parameterPath) {
        paramParent = paramParent[path];
        defaultParamParent = defaultParamParent[path];
      }
      for (let elt of data) {
        paramParent[elt[field]] = !isNaN(parseFloat(elt.Value))
          ? Math.round(parseFloat(elt.Value) * 100) / 100
          : elt.Value;
        defaultParamParent[elt[field]] = !isNaN(parseFloat(elt.Value))
          ? Math.round(parseFloat(elt.Value) * 100) / 100
          : elt.Value;
      }
    },
    chartOptions(type, units, options) {
      const { dateRange } = this;
      return ChartOptions.buildOptions(
        {
          tooltip: {
            valueSuffix: units ? ` ${units}` : null,
          },
          plotOptions: {
            bar: {
              stacking: "normal",
            },
            series: {
              pointStart: dateRange[0],
            },
          },
          ...(options || {}),
        },
        type || "column"
      );
    },
    resetParamsToDefault() {
      this.parameterValues = {
        ...this.parameterValues,
        ...this.defaultValues.parameterValues,
      };
      this.carbonTax = this.defaultValues.carbonTax;
      this.carbonTaxDate = this.defaultValues.carbonTaxDate;
      this.subsidy = this.defaultValues.subsidy;
      this.discountRate = this.defaultValues.discountRate;
      this.techCase = this.defaultValues.techCase;
    },
  },
};
</script>
<style>
.main-container {
  display: flex;
  justify-content: space-between;
}
.main-container > .container:first-child {
  margin-right: 24px;
  flex-grow: 1;
  max-width: 40%;
}
.main-container > .container:last-child {
  margin-left: 24px;
  flex-grow: 2;
  align-self: flex-start;
  position: sticky;
  top: 0;
}
@media only screen and (max-width: 991px) {
  .main-container {
    flex-direction: column;
  }
  .main-container > .container:first-child {
    max-width: 100% !important;
    margin-right: auto !important;
  }
  .main-container > .container:last-child {
    margin-left: auto !important;
    flex-grow: 2;
    align-self: flex-start;
    position: initial;
  }
}
.control-group {
  justify-content: space-between;
}
.control-group > .a-input {
  flex-grow: 0 !important;
  width: calc((100% / 2) - 6px);
  margin-bottom: 12px;
}
@media only screen and (min-width: 992px) and (max-width: 1199px) {
  .control-group > .a-input {
    width: 100% !important;
  }
}
.demand-group {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: space-between;
}
.demand-group > * {
  width: calc((100% / 2) - 6px);
}
@media only screen and (min-width: 992px) and (max-width: 1199px) {
  .demand-group > * {
    width: 100% !important;
  }
}
.technology-group {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
  justify-content: space-between;
}
.technology-group > * {
  width: calc((100% / 3) - 8px);
}
@media only screen and (min-width: 992px) and (max-width: 1199px) {
  .technology-group > * {
    width: calc((100% / 2) - 6px) !important;
  }
}
.fuel-group {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
}
.fuel-group > .m-chart-block {
  width: 100%;
}
.fuel-group > *:not(.m-chart-block) {
  width: calc((100% / 2) - 6px);
}
@media only screen and (min-width: 992px) and (max-width: 1199px) {
  .fuel-group > *:not(.m-chart-block) {
    width: 100% !important;
  }
}
.fuel-group > div:not(:last-child) {
  margin-bottom: 20px;
}
.fuel-group > div > .a-input:not(:last-child) {
  margin-bottom: 12px;
}
.radio-group {
  margin-top: 6px !important;
  flex-direction: column;
  align-items: flex-start;
}
.radio-group > * {
  margin-bottom: 12px;
}
.fuel-group .m-chart__item {
  margin: 0px !important;
}
.m-block--charts .m-chart__item {
  margin: 44px 40px 48px 20px !important;
}
.fuel-group .m-chart-block {
  margin-right: 0 !important;
}
.sub-control-group {
  margin-top: 0px !important;
}
.highcharts-stack-labels text {
  visibility: hidden;
}
.button-group {
  margin-top: 20px;
  margin-bottom: 20px;
  display: flex;
}
.button-group > button {
  flex-grow: 1;
}
.f-ui-bold {
  margin-top: 40px;
}
.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.m-nav-page__infos-close-btn {
  margin-bottom: 24px;
  margin-right: 0;
  box-shadow: none !important;
  cursor: pointer;
}
</style>
